import { Info, Loader2, Play } from "lucide-react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { APPROVED_STRIPE_CONNECT_COUNTRIES } from "@/constants/approvedCountries";
import { Route as ApplyForPartnershipRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import CampaignFAQ from "@/components/Generic/CampaignFAQ";
import CampaignVideos from "@/components/Apps/CampaignVideos";
import { Card } from "@/components/ui/card";
import { ConvexError } from "convex/values";
import { ERRORS } from "~/errors";
import { Id } from "@cvx/_generated/dataModel";
import { PARTNERSHIP_EVENTS } from "@/constants/analyticsEvents";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { Slider } from "@/components/ui/slider";
import StatusBadge from "@/components/Generic/StatusBadge";
import VideoLink from "@/components/Generic/VideoLink";
import { Route as WelcomeRoute } from "@/routes/_app/_auth/dashboard/_layout.welcome";
import { api } from "@cvx/_generated/api";
import { countries } from "countries-list";
import { createFileRoute } from "@tanstack/react-router";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";

// Component to handle video display for both storage IDs and URLs
function VideoPlayer({ video }: { video: string }) {
  // Check if it's a storage ID or URL
  const isStorageId = !video.startsWith("http");

  const { data: videoUrl } = useQuery({
    ...convexQuery(api.applications.getApplicationVideoUrl, {
      storageId: video as Id<"_storage">,
    }),
    enabled: isStorageId,
  });

  const displayUrl = isStorageId ? videoUrl : video;

  // Check if it's a YouTube URL for embedded player
  const isYouTube = video.includes("youtube.com") || video.includes("youtu.be");
  const isVimeo = video.includes("vimeo.com");
  const isLoom = video.includes("loom.com");

  if (isYouTube) {
    // Extract YouTube video ID and create embed URL
    let videoId = "";
    if (video.includes("youtu.be/")) {
      videoId = video.split("youtu.be/")[1].split("?")[0];
    } else if (video.includes("youtube.com/watch?v=")) {
      videoId = video.split("v=")[1].split("&")[0];
    } else if (video.includes("youtube.com/embed/")) {
      videoId = video.split("/embed/")[1].split("?")[0];
    }

    if (videoId) {
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  if (isVimeo) {
    // Extract Vimeo video ID and create embed URL
    const vimeoMatch = video.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen; picture-in-picture"
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  if (isLoom) {
    // Handle Loom URLs - if it's already an embed URL, use it directly
    if (video.includes("/embed/")) {
      return (
        <div className="aspect-video w-full">
          <iframe
            src={video}
            frameBorder="0"
            allowFullScreen
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
    // Convert regular Loom URLs to embed format if needed
    const loomMatch = video.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      const videoId = loomMatch[1];
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.loom.com/embed/${videoId}`}
            frameBorder="0"
            allowFullScreen
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  // For uploaded video files (storage IDs) or direct video URLs
  if (displayUrl) {
    return (
      <div className="aspect-video w-full">
        <video
          src={displayUrl}
          controls
          preload="metadata"
          className="h-full w-full rounded-lg object-contain bg-black"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Loading or error state
  return (
    <div className="aspect-video w-full bg-muted rounded-lg flex items-center justify-center">
      <div className="text-center">
        <Play className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading video...</p>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_app/campaigns/")({
  component: PublicCampaignView,
  validateSearch: (search: Record<string, unknown>) => {
    const campaignId = search.campaignId as string;
    return {
      campaignId: campaignId as Id<"campaigns">,
    };
  },
});

function PublicCampaignView() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { campaignId } = search;
  const [viewsSlider, setViewsSlider] = useState([500000]); // Default to 500k views
  const [termsAccepted, setTermsAccepted] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { setContractId } = useAccount();
  const { track } = useMixpanel();

  // Get current user to check authentication
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  const { data: campaign, isLoading } = useQuery(
    convexQuery(api.campaigns.getPublicCampaignInfo, {
      campaignId,
    }),
  );

  // Track campaign review for onboarding widget when campaign loads
  useEffect(() => {
    if (campaign && user?.creator) {
      localStorage.setItem("hasReviewedCampaign", "true");
    }
  }, [campaign, user?.creator]);

  // Fetch application data to check if user already enrolled
  const { data: applicationData } = useQuery({
    ...convexQuery(api.applications.getApplicationAndContractForCampaign, {
      campaignId: campaignId,
    }),
    enabled: !!user, // Only fetch if user is authenticated
  });

  const existingContract = applicationData?.contract;

  const { mutateAsync: joinCampaign, isPending: isJoining } = useMutation({
    mutationFn: useConvexMutation(
      api.partnerships.joinCampaignWithoutApplication,
    ),
  });

  // Check if user's country is eligible
  const isUserCountryEligible = useMemo(() => {
    if (!user?.placeOfResidence || !campaign) return true; // Allow if no country set yet

    const eligibleCountryCodes = campaign.eligibleCountries?.length
      ? campaign.eligibleCountries
      : APPROVED_STRIPE_CONNECT_COUNTRIES;

    return eligibleCountryCodes.includes(user.placeOfResidence);
  }, [user?.placeOfResidence, campaign]);

  // Get user's country name for error message
  const userCountryName = useMemo(() => {
    if (!user?.placeOfResidence) return null;
    const countryData = (countries as Record<string, { name: string }>)[
      user.placeOfResidence
    ];
    return countryData?.name || user.placeOfResidence;
  }, [user?.placeOfResidence]);

  const handleJoinCampaign = async () => {
    if (!campaign) return;

    try {
      const contractId = await joinCampaign({
        campaignId: campaign._id,
      });

      // Track campaign enrollment (no application required)
      track(PARTNERSHIP_EVENTS.CAMPAIGN_JOINED, {
        user_id: user?._id,
        campaign_id: campaign._id,
        campaign_name: campaign.name,
        app_id: campaign.appId,
        app_name: campaign.app.name,
        application_required: false,
        contract_id: contractId,
        timestamp: new Date().toISOString(),
      });

      // Invalidate the query to refresh the data and update the UI
      await queryClient.invalidateQueries({
        queryKey: convexQuery(
          api.applications.getApplicationAndContractForCampaign,
          {
            campaignId: campaign._id,
          },
        ).queryKey,
      });

      toast({
        title: "Successfully joined campaign!",
        description: "You are now part of this campaign. Welcome aboard!",
      });

      // Set contract ID and redirect to welcome screen
      setContractId(contractId);
      await navigate({
        to: WelcomeRoute.fullPath,
        search: { appName: campaign.app.name },
        replace: true,
      });
    } catch (error) {
      console.error("Join campaign error:", error);
      toast({
        title: "Failed to join campaign",
        description:
          error instanceof ConvexError
            ? error.data
            : ERRORS.SOMETHING_WENT_WRONG,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex h-full w-full items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary">
            Campaign not found
          </h1>
          <p className="mt-2 text-primary/60">
            The campaign you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-primary">
            {campaign.type === "challenge"
              ? "Join the Challenge"
              : "Partnership Opportunity"}
          </h2>
          <p className="text-base text-primary/60">
            Partner with {campaign.app.name}
          </p>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                className="h-8 w-8 rounded-full object-cover"
                alt={campaign.app.name}
                src={campaign.app.logo}
              />
              <div>
                <h3 className="text-xl font-semibold text-primary">
                  {campaign.app.name}
                </h3>
                <p className="text-sm text-primary/60">
                  {campaign.app.description}
                </p>
              </div>
            </div>
            <div>
              {campaign.status === "ended" ? (
                <StatusBadge status="ended" />
              ) : campaign.endDate &&
                Math.ceil(
                  (campaign.endDate - Date.now()) / (1000 * 60 * 60 * 24),
                ) <= 5 ? (
                <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 border-0 min-w-[90px] text-center whitespace-nowrap">
                  {Math.ceil(
                    (campaign.endDate - Date.now()) / (1000 * 60 * 60 * 24),
                  )}{" "}
                  days left
                </Badge>
              ) : (
                <StatusBadge status="active" />
              )}
            </div>
          </div>

          <div className="mt-6 space-y-6">
            <div>
              <h4 className="font-semibold text-primary text-base">
                {campaign.type === "challenge"
                  ? `About ${campaign.app.name}`
                  : "What We're Looking For"}
              </h4>
              {campaign.videos && campaign.videos.length > 0 && (
                <div className="mt-4">
                  <VideoPlayer video={campaign.videos[0]} />
                </div>
              )}
              {campaign.creatorBrief ? (
                <div
                  className="mt-2 text-sm text-primary/60 rich-text-display"
                  dangerouslySetInnerHTML={{ __html: campaign.creatorBrief }}
                />
              ) : (
                <p className="mt-2 text-sm text-primary/60">
                  No brief provided
                </p>
              )}
            </div>

            {/* How Challenges Work - Only for challenge campaigns */}
            {campaign.type === "challenge" && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-primary text-base mb-3">
                  How Challenges Work
                </h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      1.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You submit a piece of content
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      2.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      It gets reviewed and approved by the brand
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      3.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You post
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      4.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You start earning
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Allowed Platforms */}
            {campaign.allowedChannels &&
              campaign.allowedChannels.length > 0 && (
                <div>
                  <h4 className="font-semibold text-primary text-base">
                    Allowed Platforms
                  </h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {campaign.allowedChannels.map((channel) => (
                      <div
                        key={channel}
                        className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                      >
                        <PlatformIcon platform={channel} />
                        <span className="text-sm font-medium capitalize">
                          {channel}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Predefined CPM */}
            {campaign.predefinedCpm && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Payment Rate
                </h4>
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="text-sm text-green-800 dark:text-green-300">
                    ${campaign.predefinedCpm} CPM (per 1,000 views)
                  </span>
                </div>

                {/* Earnings Calculator - Only for Challenge Campaigns */}
                {campaign.type === "challenge" && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                    <h5 className="font-medium text-sm text-primary mb-3">
                      Earnings Calculator
                    </h5>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-primary/60">Views</span>
                        <span className="text-sm font-medium text-primary">
                          {viewsSlider[0].toLocaleString()}
                        </span>
                      </div>
                      <Slider
                        value={viewsSlider}
                        onValueChange={setViewsSlider}
                        max={1000000}
                        min={0}
                        step={10000}
                        className="w-full"
                      />
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-primary/60">
                          Potential Earnings
                        </span>
                        <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                          $
                          {(
                            (viewsSlider[0] / 1000) *
                            campaign.predefinedCpm
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Validation Requirements */}
            {campaign.validationRequirements && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Content Requirements
                </h4>
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div
                    className="text-sm text-blue-800 dark:text-blue-300 rich-text-display"
                    dangerouslySetInnerHTML={{
                      __html: campaign.validationRequirements,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Campaign Budget */}
            {campaign.prizePool && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Campaign Budget
                </h4>
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Total Budget Cap
                    </span>
                    <span className="text-lg font-semibold text-amber-900 dark:text-amber-200">
                      ${campaign.prizePool.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                    Once approved video earnings reach this amount, new posts
                    will be disabled and views will be frozen.
                  </p>
                </div>
              </div>
            )}

            {(campaign.budgetCpmMin ||
              campaign.budgetCpmMax ||
              campaign.budgetFlatFeeMin ||
              campaign.budgetFlatFeeMax) && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Budget Ranges
                </h4>
                <div className="mt-2 space-y-2">
                  {(campaign.budgetCpmMin || campaign.budgetCpmMax) && (
                    <p className="text-sm text-primary/60">
                      CPM: ${campaign.budgetCpmMin ?? 0} - $
                      {campaign.budgetCpmMax}
                    </p>
                  )}
                  {(campaign.budgetFlatFeeMin || campaign.budgetFlatFeeMax) && (
                    <p className="text-sm text-primary/60">
                      Flat Fee: ${campaign.budgetFlatFeeMin ?? 0} - $
                      {campaign.budgetFlatFeeMax}
                    </p>
                  )}
                </div>
              </div>
            )}

            {campaign.urls && campaign.urls.length > 0 && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Useful Links
                </h4>
                <div className="mt-2 space-y-4">
                  {campaign.urls.map((urlItem, index) => (
                    <VideoLink
                      key={index}
                      url={urlItem.url}
                      title={urlItem.title}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Campaign Marketing Videos */}
            {campaign.videos && campaign.videos.length > 1 && (
              <CampaignVideos
                videos={campaign.videos.slice(1)}
                title="Walkthroughs"
              />
            )}

            {/* Eligible Countries Section */}
            <div>
              <h4 className="font-semibold text-primary text-base">
                Eligible Countries
              </h4>
              <p className="text-sm text-primary/60 mb-3">
                This campaign is available to creators located in the following
                countries:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Object.entries(
                  countries as { [code: string]: { name: string } },
                )
                  .filter(([code]) =>
                    campaign.eligibleCountries?.length
                      ? campaign.eligibleCountries.includes(code)
                      : APPROVED_STRIPE_CONNECT_COUNTRIES.includes(code),
                  )
                  .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                  .map(([code, country]) => (
                    <div
                      key={code}
                      className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                    >
                      <span className="text-lg">
                        {getUnicodeFlagIcon(code)}
                      </span>
                      <span className="text-sm font-medium">
                        {country.name}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg mt-3">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">Payment Requirements:</p>
                  <p>
                    To receive payments, you must be located in one of these
                    countries and have valid banking information. This ensures
                    we can process payments securely and comply with
                    international regulations.
                  </p>
                </div>
              </div>
            </div>

            {/* Key Terms & Conditions */}
            <div className="pt-6 border-t">
              <h4 className="font-semibold text-primary text-base mb-3">
                Key Terms & Conditions
              </h4>
              <div className="space-y-3 text-sm text-primary/70">
                {campaign.predefinedCpm && (
                  <p>
                    • You'll receive ${campaign.predefinedCpm} for every 1,000
                    views for every video that gets approved by{" "}
                    {campaign.app.name} team. To qualify for this CPM reward,
                    you must post your video before the campaign ends. From the
                    time you post, any view up to 7 days counts.
                    {campaign.prizePool && (
                      <>
                        {" "}
                        Please recall that budget on this campaign is limited.
                        We’d recommend posting as soon as your video gets
                        approved to ensure you don’t miss out on available
                        budget.
                      </>
                    )}
                  </p>
                )}
                <p>
                  • Payment will be delivered within 14 days of the view count
                  window having ended. We can only provide payments via Stripe
                  Connect, so we'll ask you to enroll into Stripe Connect before
                  you submit your first piece of content.
                </p>
                <p>
                  • Boosting content through ads or bot activity is not allowed;
                  any video that breaches this will be disqualified and receive
                  no remuneration.
                </p>
                <p>
                  • Any approved content will need to be published with a
                  hashtag{" "}
                  <span className="font-mono text-xs text-primary">#ad</span> in
                  order to qualify for the CPM reward.
                </p>
                <p>
                  • Content that you submit can be promoted by{" "}
                  {campaign.app.name} on its own channels, incl. through ads. By
                  submitting a video, you grant {campaign.app.name} the license
                  to do this.
                </p>
                <p className="mt-3">
                  For more information about T&Cs,{" "}
                  <a
                    href="/terms"
                    className="text-primary hover:underline font-medium"
                  >
                    click here
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </Card>

        {campaign.status === "ended" ? (
          <>
            <div className="text-center mb-4 p-4 bg-muted rounded-lg">
              <p className="text-lg font-semibold text-primary">
                This campaign has ended
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Thank you for your interest. This opportunity is no longer
                accepting applications.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                window.open("https://tally.so/r/wAeB8z", "_blank");
              }}
            >
              Join Waitlist for More Opportunities
            </Button>
          </>
        ) : user &&
          campaign.applicationNotRequired &&
          existingContract &&
          (existingContract.status === "active" ||
            existingContract.status === "pendingApproval") ? (
          // User is authenticated, campaign doesn't require application, and user is already enrolled
          <div className="space-y-4">
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Info className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-lg font-semibold text-green-800 dark:text-green-300">
                  {existingContract.status === "active"
                    ? "You're already part of this campaign!"
                    : "Partnership pending approval"}
                </p>
              </div>
              <p className="text-sm text-green-700 dark:text-green-400">
                {existingContract.status === "active"
                  ? "You can start creating content and earning from this campaign."
                  : "Please wait for the partnership terms to be approved."}
              </p>
            </div>
          </div>
        ) : user && campaign.applicationNotRequired ? (
          // User is authenticated and campaign doesn't require application - allow direct enrollment
          <>
            {/* Terms and Conditions Checkbox */}
            <div className="flex items-start gap-2 p-4 bg-muted/50 rounded-lg">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <label
                htmlFor="terms-checkbox"
                className="text-sm text-primary/80 cursor-pointer"
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  className="text-primary hover:underline font-medium"
                >
                  Terms and Conditions
                </a>
              </label>
            </div>

            <Button
              className="w-full"
              onClick={handleJoinCampaign}
              disabled={isJoining || !isUserCountryEligible || !termsAccepted}
            >
              {isJoining ? (
                <>
                  <Loader2 className="animate-spin mr-2" />
                  Enrolling...
                </>
              ) : campaign.type === "challenge" ? (
                "Get Started"
              ) : (
                "Join Campaign"
              )}
            </Button>

            {/* Show eligibility error below button */}
            {!isUserCountryEligible && (
              <div className="mt-3 p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/20">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      This campaign is not available in{" "}
                      {userCountryName || "your country"}. Please check the
                      eligible countries list above.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          // Either user is not authenticated OR campaign requires application - use original flow
          <>
            <Button
              className="w-full"
              onClick={() => {
                // For non-authenticated users, store the campaign ID for after authentication
                if (!user) {
                  const { setPendingCampaignId } = useAccount.getState();
                  setPendingCampaignId(campaign._id);
                }

                navigate({
                  to: ApplyForPartnershipRoute.fullPath,
                  search: { selectedCampaignId: campaign._id },
                });
              }}
            >
              {campaign.type === "challenge"
                ? "Get Started"
                : "Apply for Partnership"}
            </Button>
            <p className="mt-4 text-sm text-muted-foreground text-center">
              By proceeding, you agree to our{" "}
              <a href="/terms" className="text-primary hover:underline">
                Terms and Conditions
              </a>
            </p>
          </>
        )}

        {/* FAQ Section */}
        {campaign.type === "challenge" && <CampaignFAQ />}
      </div>
    </div>
  );
}
